import { createContext, useContext } from "react";
import { Outlet } from "react-router";
import { useProjectContext } from "@/app/project-context";
import { useChatStream } from "./use-chat-stream";

/**
 * The answer this tab is watching being written, held above the Brain's tabs
 * (2026-09-27).
 *
 * **Why here and not in the Ask screen.** The History tab is a route of its
 * own (`product/0013`), so going there unmounts the Ask screen. With the
 * stream in the screen, that threw it away: a question opened from History
 * while another was still being written showed no "Still answering your other
 * question", its Ask stayed enabled, and a second question ran beside the
 * first instead of stopping it (`product/0005` §2.6). The list used to sit
 * inside the Ask screen, so none of this happened before the tab existed.
 *
 * **Not worked out from the question list instead.** The list is the whole
 * project's, every person's questions, and says nothing about who asked. A
 * question still running there may be somebody else's, and blocking Ask on it,
 * or stopping it on the next ask, would be wrong. This tab's own stream is
 * the one thing known to be this reader's.
 *
 * A layout route (`routes.tsx`) around every `brain/*` route, so a source
 * card's file, an entry and the other tabs keep it too. Keyed by project:
 * the layout is not remounted when the project changes, and a stream of one
 * project's question has no place in another's tabs.
 */

type AskStream = ReturnType<typeof useChatStream>;

const AskStreamContext = createContext<AskStream | null>(null);

export function AskStreamScope() {
  const { projectId } = useProjectContext();
  return <AskStreamHolder key={projectId} projectId={projectId} />;
}

function AskStreamHolder({ projectId }: { projectId: string }) {
  const stream = useChatStream(projectId);
  return (
    <AskStreamContext.Provider value={stream}>
      <Outlet />
    </AskStreamContext.Provider>
  );
}

/** The stream the Ask screen shows and asks through. Only inside `AskStreamScope`. */
export function useAskStream(): AskStream {
  const stream = useContext(AskStreamContext);
  if (!stream) throw new Error("useAskStream is used outside AskStreamScope (see routes.tsx).");
  return stream;
}
